// Firebase functions for company-related operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp,
  writeBatch,
  getCountFromServer
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./firebase-config";

/**
 * Fast-track a company for a user
 * @param {string} userId - The user ID
 * @param {string} companyId - The company ID to fast-track
 * @param {string} notes - Optional notes about the fast-track action
 * @returns {Promise<object>} - Success status and data or error
 */
export const fastTrackCompany = async (userId, companyId, notes = 'User initiated fast-track') => {
  try {
    if (!userId || !companyId) {
      return { success: false, error: 'Missing required parameters' };
    }

    const userProfileRef = doc(db, 'user_profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    const timestamp = new Date().toISOString();
    const newStatus = 'fast-tracked';
    
    if (userProfileSnap.exists()) {
      const userData = userProfileSnap.data();
      const companyStatuses = userData.companyStatuses || {};
      const companyStatusHistory = userData.companyStatusHistory || {};
      
      // Get previous status if any
      const previousStatus = companyStatuses[companyId] || null;
      
      // Create history entry
      const historyEntry = {
        status: newStatus,
        previousStatus,
        timestamp,
        notes
      };
      
      // Update statuses and history
      const updatedCompanyStatuses = {
        ...companyStatuses,
        [companyId]: newStatus
      };
      
      const updatedCompanyStatusHistory = {
        ...companyStatusHistory,
        [companyId]: companyStatusHistory[companyId] 
          ? [...companyStatusHistory[companyId], historyEntry]
          : [historyEntry]
      };
      
      // Update user profile
      await updateDoc(userProfileRef, {
        companyStatuses: updatedCompanyStatuses,
        companyStatusHistory: updatedCompanyStatusHistory
      });
      
      return { 
        success: true, 
        data: { 
          companyId,
          status: newStatus,
          timestamp
        } 
      };
    } else {
      // Create user profile if it doesn't exist
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      const companyStatuses = { [companyId]: newStatus };
      const companyStatusHistory = {
        [companyId]: [{
          status: newStatus,
          previousStatus: null,
          timestamp,
          notes
        }]
      };
      
      await setDoc(userProfileRef, {
        companyStatuses,
        companyStatusHistory,
        email: currentUser.email,
        displayName: currentUser.displayName || 'Anonymous User',
        createdAt: timestamp
      });
      
      return { 
        success: true, 
        data: { 
          companyId,
          status: newStatus,
          timestamp
        } 
      };
    }
  } catch (error) {
    console.error('Error in fastTrackCompany:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to fast-track company' 
    };
  }
};

/**
 * Get companies with pagination and filtering options
 * @param {object} options - Query options
 * @param {number} options.limit - Number of results to return
 * @param {string} options.lastVisible - Last document for pagination
 * @param {string} options.industry - Filter by industry
 * @param {string} options.searchTerm - Search term for company name
 * @returns {Promise<object>} - Success status and companies data or error
 */
export const getCompanies = async (options = {}) => {
  try {
    const { 
      limit: limitCount = 12, 
      lastVisible = null,
      industry = null,
      searchTerm = null,
      orderByField = 'createdAt',
      orderDirection = 'desc'
    } = options;
    
    let companiesQuery = collection(db, 'companies');
    
    // Build the query
    const queryConstraints = [];
    
    // Add filters if provided
    if (industry) {
      queryConstraints.push(where('industry', '==', industry));
    }
    
    // Add ordering
    queryConstraints.push(orderBy(orderByField, orderDirection));
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit
    queryConstraints.push(limit(limitCount));
    
    // Apply constraints
    companiesQuery = query(companiesQuery, ...queryConstraints);
    
    // Execute query
    const companiesSnapshot = await getDocs(companiesQuery);
    
    if (companiesSnapshot.empty) {
      return { 
        success: true, 
        data: {
          companies: [],
          lastVisible: null,
          hasMore: false
        }
      };
    }
    
    // Get last document for pagination
    const lastDoc = companiesSnapshot.docs[companiesSnapshot.docs.length - 1];
    
    // Transform data
    let companies = companiesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by search term if provided
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      companies = companies.filter(company => 
        company.name?.toLowerCase().includes(term) || 
        company.description?.toLowerCase().includes(term)
      );
    }
    
    return { 
      success: true, 
      data: {
        companies,
        lastVisible: lastDoc,
        hasMore: companies.length === limitCount
      }
    };
  } catch (error) {
    console.error('Error in getCompanies:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to fetch companies' 
    };
  }
};

/**
 * Mark a company as contacted by an admin for a candidate
 * @param {string} candidateUserId - The ID of the candidate user
 * @param {string} companyId - The ID of the company contacted
 * @param {string} companyName - The name of the company contacted
 * @param {string} notes - Optional notes about the outreach
 * @param {Object} adminInfo - Optional info about the assigned admin
 * @param {Object} contactInfo - Optional info about the contact person
 * @returns {Promise<object>} - Success status and data or error
 */
export const markCompanyContacted = async (candidateUserId, companyId, companyName, notes = '', adminInfo = null, contactInfo = null) => {
  try {
    console.log('Marking company as contacted:', { candidateUserId, companyId, companyName, notes, adminInfo, contactInfo });
    
    if (!candidateUserId || !companyId) {
      return { success: false, error: 'Missing required parameters' };
    }

    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }
    
    const userProfileRef = doc(db, 'user_profiles', candidateUserId);
    
    // Prepare update payload
    const updatePayload = {};
    
    // Determine which admin to use for the contact
    const contactAdminUid = adminInfo?.adminUid || adminUser.uid;
    
    // Update contactOutreach with contact information if provided
    updatePayload[`contactOutreach.${companyId}`] = {
      contactedAt: serverTimestamp(),
      contactedByAdminUid: contactAdminUid,
      contactNotes: notes,
      companyName: companyName,
      status: 'contacted',
      // Add contact info if available
      ...(contactInfo && {
        contactId: contactInfo.contactId,
        contactName: contactInfo.contactName,
        contactEmail: contactInfo.contactEmail
      })
    };
    
    // Update companyStatuses
    updatePayload[`companyStatuses.${companyId}`] = 'contact-established';
    
    // Add to companyStatusHistory - Use Date instead of serverTimestamp() for array items
    const now = new Date();
    const historyEntry = {
      status: 'admin_contacted_company',
      timestamp: now.toISOString(), // Use ISO string instead of serverTimestamp
      adminUid: contactAdminUid,
      updatedByAdminUid: adminUser.uid,
      updatedByAdminEmail: adminUser.email,
      notes: `Admin initiated contact with ${companyName}.${notes ? ' Notes: ' + notes : ''}`,
      type: 'admin_action',
      // Add contact info to history if available
      ...(contactInfo && {
        contactId: contactInfo.contactId,
        contactName: contactInfo.contactName,
        contactEmail: contactInfo.contactEmail
      })
    };
    
    // Get current companyStatusHistory to properly update the array
    const userDoc = await getDoc(userProfileRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentHistory = userData.companyStatusHistory || {};
      const companyHistory = currentHistory[companyId] || [];
      
      // Add new entry to history array
      const updatedHistory = [...companyHistory, historyEntry];
      updatePayload[`companyStatusHistory.${companyId}`] = updatedHistory;
    } else {
      // If user profile doesn't exist yet, create with initial history
      updatePayload[`companyStatusHistory.${companyId}`] = [historyEntry];
    }
    
    // Update user profile
    await updateDoc(userProfileRef, updatePayload);
    
    // If contact info is provided, update the contact document to associate it with this company
    if (contactInfo && contactInfo.contactId) {
      try {
        const contactRef = doc(db, 'contacts', contactInfo.contactId);
        const contactDoc = await getDoc(contactRef);
        
        if (contactDoc.exists()) {
          const contactData = contactDoc.data();
          
          // Always update the contact with the company information
          const updateData = {
            updatedAt: serverTimestamp(),
            updatedBy: adminUser.email || adminUser.uid
          };
          
          // Add company to the companyIds array if it doesn't exist
          if (Array.isArray(contactData.companyIds)) {
            if (!contactData.companyIds.includes(companyId)) {
              updateData.companyIds = [...contactData.companyIds, companyId];
            }
          } else {
            // Create the companyIds array if it doesn't exist
            updateData.companyIds = [companyId];
          }
          
          // Also set or update the company field if needed
          if (!contactData.company || contactData.company === '') {
            updateData.company = companyName || companyId;
          }
          
          // Update the contact document
          await updateDoc(contactRef, updateData);
          console.log(`Updated contact ${contactInfo.contactId} with company association to ${companyId}`);
        } else {
          console.error(`Contact with ID ${contactInfo.contactId} not found`);
        }
      } catch (error) {
        console.error('Error updating contact with company association:', error);
        // Don't fail the entire operation if this part fails
      }
    }
    
    return {
      success: true,
      data: {
        companyId,
        companyName,
        contactedAt: now,
        contactedByAdmin: adminInfo?.adminEmail || adminUser.email || adminUser.uid,
        contactInfo: contactInfo || null
      }
    };
  } catch (error) {
    console.error('Error marking company as contacted:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark company as contacted'
    };
  }
};

/**
 * Add a new status entry to a user's company status history
 * @param {string} userId - The ID of the user
 * @param {string} companyId - The ID of the company
 * @param {string} status - The new status to add
 * @param {Object} metadata - Additional metadata to include in the status entry
 * @returns {Promise<object>} - Success status and data or error
 */
export const updateCompanyStatusHistory = async (userId, companyId, status, metadata = {}) => {
  try {
    console.log('Updating company status history:', { userId, companyId, status, metadata });
    
    if (!userId || !companyId || !status) {
      return { success: false, error: 'Missing required parameters' };
    }

    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }
    
    const userProfileRef = doc(db, 'user_profiles', userId);
    
    // Prepare the history entry
    // Use regular Date instead of serverTimestamp() as serverTimestamp() is not supported inside arrays
    const now = new Date();
    
    // Track both the admin who made the change and the admin who is assigned
    const historyEntry = {
      status: status,
      timestamp: now.toISOString(), // Use ISO string format instead of serverTimestamp()
      updatedByAdminUid: adminUser.uid, // The admin who made the update/change
      updatedByAdminEmail: adminUser.email,
      // Use assigned admin details from metadata instead of the current user
      adminUid: metadata.assignedAdminUid || adminUser.uid, // If not specified, fallback to current user
      assignedAdminEmail: metadata.assignedAdminEmail,
      assignedAdminName: metadata.assignedAdminName,
      ...metadata,
      type: 'admin_action'
    };
    
    // Remove duplicate fields if they were passed in metadata
    delete historyEntry.assignedAdminUid; // Use adminUid for consistency
    
    // Get current companyStatusHistory to properly update the array
    const userDoc = await getDoc(userProfileRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentHistory = userData.companyStatusHistory || {};
      const companyHistory = currentHistory[companyId] || [];
      
      // Add new entry to history array
      const updatedHistory = [...companyHistory, historyEntry];
      
      // Update user profile
      await updateDoc(userProfileRef, {
        [`companyStatusHistory.${companyId}`]: updatedHistory
      });
      
      return {
        success: true,
        data: {
          companyId,
          status,
          timestamp: now
        }
      };
    } else {
      return { success: false, error: 'User profile not found' };
    }
  } catch (error) {
    console.error('Error updating company status history:', error);
    return {
      success: false,
      error: error.message || 'Failed to update company status'
    };
  }
};
// =========== ADMIN COMPANY MANAGEMENT OPERATIONS ============

/**
 * Create a new company (Admin only)
 * @param {Object} companyData - Company data to create
 * @returns {Promise<object>} - Success status and company data or error
 */
export const createCompany = async (companyData) => {
  try {
    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }

    // Validate required fields
    if (!companyData.name || !companyData.industry) {
      return { success: false, error: 'Company name and industry are required' };
    }

    // Create company document
    const companyRef = doc(collection(db, 'companies'));
    const now = serverTimestamp();
    
    const newCompany = {
      ...companyData,
      id: companyRef.id,
      createdAt: now,
      updatedAt: now,
      createdBy: adminUser.uid,
      updatedBy: adminUser.uid
    };

    await setDoc(companyRef, newCompany);

    return {
      success: true,
      data: {
        id: companyRef.id,
        ...newCompany
      }
    };
  } catch (error) {
    console.error('Error creating company:', error);
    return {
      success: false,
      error: error.message || 'Failed to create company'
    };
  }
};

/**
 * Update an existing company (Admin only)
 * @param {string} companyId - Company ID to update
 * @param {Object} updateData - Data to update
 * @returns {Promise<object>} - Success status and updated data or error
 */
export const updateCompany = async (companyId, updateData) => {
  try {
    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }

    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);

    if (!companySnap.exists()) {
      return { success: false, error: 'Company not found' };
    }

    const updatedData = {
      ...updateData,
      updatedAt: serverTimestamp(),
      updatedBy: adminUser.uid
    };

    await updateDoc(companyRef, updatedData);

    return {
      success: true,
      data: {
        id: companyId,
        ...updatedData
      }
    };
  } catch (error) {
    console.error('Error updating company:', error);
    return {
      success: false,
      error: error.message || 'Failed to update company'
    };
  }
};

/**
 * Delete a company (Admin only)
 * @param {string} companyId - Company ID to delete
 * @returns {Promise<object>} - Success status or error
 */
export const deleteCompany = async (companyId) => {
  try {
    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }

    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);

    if (!companySnap.exists()) {
      return { success: false, error: 'Company not found' };
    }

    await deleteDoc(companyRef);

    return {
      success: true,
      data: { companyId }
    };
  } catch (error) {
    console.error('Error deleting company:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete company'
    };
  }
};

/**
 * Get a single company by ID
 * @param {string} companyId - Company ID to fetch
 * @returns {Promise<object>} - Success status and company data or error
 */
export const getCompanyById = async (companyId) => {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);

    if (!companySnap.exists()) {
      return { success: false, error: 'Company not found' };
    }

    return {
      success: true,
      data: {
        id: companySnap.id,
        ...companySnap.data()
      }
    };
  } catch (error) {
    console.error('Error fetching company:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch company'
    };
  }
};

/**
 * Get companies with enhanced admin features
 * @param {object} options - Query options
 * @returns {Promise<object>} - Success status and companies data or error
 */
export const getCompaniesForAdmin = async (options = {}) => {
  try {
    const { 
      limit: limitCount = 50, 
      lastVisible = null,
      industry = null,
      searchTerm = null,
      orderByField = 'createdAt',
      orderDirection = 'desc',
      includeStats = true
    } = options;
    
    let companiesQuery = collection(db, 'companies');
    
    // Build the query
    const queryConstraints = [];
    
    // Add filters if provided
    if (industry) {
      queryConstraints.push(where('industry', '==', industry));
    }
    
    // Add ordering
    queryConstraints.push(orderBy(orderByField, orderDirection));
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit
    queryConstraints.push(limit(limitCount));
    
    // Apply constraints
    companiesQuery = query(companiesQuery, ...queryConstraints);
    
    // Execute query
    const companiesSnapshot = await getDocs(companiesQuery);
    
    if (companiesSnapshot.empty) {
      return { 
        success: true, 
        data: {
          companies: [],
          lastVisible: null,
          hasMore: false,
          total: 0
        }
      };
    }
    
    // Get last document for pagination
    const lastDoc = companiesSnapshot.docs[companiesSnapshot.docs.length - 1];
    
    // Transform data
    let companies = companiesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by search term if provided (client-side for now)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      companies = companies.filter(company => 
        company.name?.toLowerCase().includes(term) || 
        company.description?.toLowerCase().includes(term) ||
        company.location?.toLowerCase().includes(term)
      );
    }

    // Add usage statistics if requested
    if (includeStats) {
      companies = await Promise.all(companies.map(async (company) => {
        const stats = await getCompanyUsageStats(company.id);
        return {
          ...company,
          userCount: stats.userCount || 0,
          jobCount: stats.jobCount || 0,
          lastActivity: stats.lastActivity || null
        };
      }));
    }

    // Get total count for pagination info
    const totalSnapshot = await getCountFromServer(collection(db, 'companies'));
    const total = totalSnapshot.data().count;
    
    return { 
      success: true, 
      data: {
        companies,
        lastVisible: lastDoc,
        hasMore: companies.length === limitCount,
        total
      }
    };
  } catch (error) {
    console.error('Error in getCompaniesForAdmin:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to fetch companies' 
    };
  }
};

/**
 * Get usage statistics for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<object>} - Usage statistics
 */
export const getCompanyUsageStats = async (companyId) => {
  try {
    if (!companyId) {
      return { userCount: 0, jobCount: 0, lastActivity: null };
    }

    // Count users associated with this company
    const userProfilesRef = collection(db, 'user_profiles');
    const userQuery = query(userProfilesRef, where('company', '==', companyId));
    const userSnapshot = await getCountFromServer(userQuery);
    const userCount = userSnapshot.data().count;

    // Count jobs posted by this company
    const jobsRef = collection(db, 'jobs');
    const jobQuery = query(jobsRef, where('companyId', '==', companyId));
    const jobSnapshot = await getCountFromServer(jobQuery);
    const jobCount = jobSnapshot.data().count;

    // Get last activity (most recent job posting or user registration)
    let lastActivity = null;
    try {
      const recentJobQuery = query(
        jobsRef, 
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      const recentJobSnapshot = await getDocs(recentJobQuery);
      
      if (!recentJobSnapshot.empty) {
        lastActivity = recentJobSnapshot.docs[0].data().createdAt;
      }
    } catch (error) {
      console.warn('Could not fetch last activity:', error);
    }

    return {
      userCount,
      jobCount,
      lastActivity
    };
  } catch (error) {
    console.error('Error getting company usage stats:', error);
    return { userCount: 0, jobCount: 0, lastActivity: null };
  }
};

/**
 * Bulk delete companies (Admin only)
 * @param {string[]} companyIds - Array of company IDs to delete
 * @returns {Promise<object>} - Success status and results or error
 */
export const bulkDeleteCompanies = async (companyIds) => {
  try {
    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }

    if (!companyIds || companyIds.length === 0) {
      return { success: false, error: 'No company IDs provided' };
    }

    const batch = writeBatch(db);
    const results = {
      successful: [],
      failed: []
    };

    // Process each company
    for (const companyId of companyIds) {
      try {
        const companyRef = doc(db, 'companies', companyId);
        const companySnap = await getDoc(companyRef);

        if (companySnap.exists()) {
          batch.delete(companyRef);
          results.successful.push(companyId);
        } else {
          results.failed.push({ companyId, error: 'Company not found' });
        }
      } catch (error) {
        results.failed.push({ companyId, error: error.message });
      }
    }

    // Commit the batch
    if (results.successful.length > 0) {
      await batch.commit();
    }

    return {
      success: true,
      data: {
        totalProcessed: companyIds.length,
        successful: results.successful.length,
        failed: results.failed.length,
        results
      }
    };
  } catch (error) {
    console.error('Error in bulk delete companies:', error);
    return {
      success: false,
      error: error.message || 'Failed to bulk delete companies'
    };
  }
};

/**
 * Get cascade information for company deletion
 * @param {string} companyId - Company ID
 * @returns {Promise<object>} - Cascade impact information
 */
export const getCompanyDeletionImpact = async (companyId) => {
  try {
    if (!companyId) {
      return { userCount: 0, jobCount: 0, contactCount: 0 };
    }

    // Get usage stats (includes user and job counts)
    const stats = await getCompanyUsageStats(companyId);

    // Count contacts associated with this company
    let contactCount = 0;
    try {
      const contactsRef = collection(db, 'contacts');
      const contactQuery = query(contactsRef, where('companyIds', 'array-contains', companyId));
      const contactSnapshot = await getCountFromServer(contactQuery);
      contactCount = contactSnapshot.data().count;
    } catch (error) {
      console.warn('Could not count contacts:', error);
    }

    return {
      userCount: stats.userCount,
      jobCount: stats.jobCount,
      contactCount
    };
  } catch (error) {
    console.error('Error getting company deletion impact:', error);
    return { userCount: 0, jobCount: 0, contactCount: 0 };
  }
};

/**
 * Export companies data (Admin only)
 * @param {string[]} companyIds - Optional array of specific company IDs to export
 * @returns {Promise<object>} - Success status and export data or error
 */
export const exportCompanies = async (companyIds = null) => {
  try {
    const auth = getAuth();
    const adminUser = auth.currentUser;
    
    if (!adminUser) {
      return { success: false, error: 'Admin authentication required' };
    }

    let companiesQuery;
    
    if (companyIds && companyIds.length > 0) {
      // Export specific companies
      const companies = [];
      for (const companyId of companyIds) {
        const companyResult = await getCompanyById(companyId);
        if (companyResult.success) {
          companies.push(companyResult.data);
        }
      }
      
      return {
        success: true,
        data: {
          companies,
          exportedAt: new Date().toISOString(),
          exportedBy: adminUser.email || adminUser.uid
        }
      };
    } else {
      // Export all companies
      const result = await getCompaniesForAdmin({ limit: 1000, includeStats: true });
      
      if (!result.success) {
        return result;
      }

      return {
        success: true,
        data: {
          companies: result.data.companies,
          total: result.data.total,
          exportedAt: new Date().toISOString(),
          exportedBy: adminUser.email || adminUser.uid
        }
      };
    }
  } catch (error) {
    console.error('Error exporting companies:', error);
    return {
      success: false,
      error: error.message || 'Failed to export companies'
    };
  }
};